import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { MdContentCopy, MdDeleteOutline, MdKey, MdRefresh, MdSave, MdSend } from 'react-icons/md'
import ConfirmModal from '@/components/confirm-modal'
import {
    getVideoWebhook,
    listVideoWebhookDeliveries,
    removeVideoWebhook,
    rotateVideoWebhookSecret,
    saveVideoWebhook,
    testVideoWebhook,
    VIDEO_WEBHOOK_EVENT_LABELS,
    type VideoWebhookDelivery,
    type VideoWebhookEvent,
} from '@/lib/api/video/video-api'

const EVENTS = Object.keys(VIDEO_WEBHOOK_EVENT_LABELS) as VideoWebhookEvent[]

const DELIVERY_STATUS: Record<VideoWebhookDelivery['status'], { label: string; className: string }> = {
    PENDING: { label: 'Tentando', className: 'badge-warning' },
    SUCCESS: { label: 'Entregue', className: 'badge-success' },
    FAILED: { label: 'Falhou', className: 'badge-error' },
}

function formatDateTime(value: string): string {
    return new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'medium' })
}

/**
 * Avisos para o sistema do cliente (webhook): endereço, eventos, segredo de
 * assinatura (mostrado uma vez), teste e histórico de entregas.
 */
export default function VideoWebhookSection({ editable }: { editable: boolean }) {
    const [loaded, setLoaded] = useState(false)
    const [exists, setExists] = useState(false)
    const [url, setUrl] = useState('')
    const [events, setEvents] = useState<VideoWebhookEvent[]>(EVENTS)
    const [enabled, setEnabled] = useState(true)
    const [secret, setSecret] = useState<string | null>(null)
    const [deliveries, setDeliveries] = useState<VideoWebhookDelivery[]>([])
    const [busy, setBusy] = useState<'save' | 'test' | 'rotate' | 'remove' | null>(null)
    const [confirm, setConfirm] = useState<'rotate' | 'remove' | null>(null)

    const loadDeliveries = useCallback(async () => {
        const response = await listVideoWebhookDeliveries()
        if (response.success && response.result) setDeliveries(response.result)
    }, [])

    useEffect(() => {
        void getVideoWebhook().then(response => {
            if (response.success && response.result) {
                setExists(true)
                setUrl(response.result.url)
                // Vazio no servidor = todos.
                setEvents(response.result.events.length > 0 ? response.result.events : EVENTS)
                setEnabled(response.result.enabled)
                void loadDeliveries()
            }
            setLoaded(true)
        })
    }, [loadDeliveries])

    if (!loaded) return null

    const toggleEvent = (event: VideoWebhookEvent) =>
        setEvents(current => (current.includes(event) ? current.filter(item => item !== event) : [...current, event]))

    const save = async () => {
        if (!url.trim()) {
            toast.error('Informe o endereço https do seu sistema')
            return
        }
        if (events.length === 0) {
            toast.error('Escolha ao menos um evento')
            return
        }
        setBusy('save')
        // Todos marcados = vazio no servidor (assina também eventos futuros).
        const response = await saveVideoWebhook({
            url: url.trim(),
            events: events.length === EVENTS.length ? [] : events,
            enabled,
        })
        setBusy(null)
        if (!response.success || !response.result) {
            toast.error(response.error || 'Não foi possível salvar o webhook')
            return
        }
        setExists(true)
        setUrl(response.result.url)
        if (response.result.secret) setSecret(response.result.secret)
        toast.success('Webhook salvo')
    }

    const test = async () => {
        setBusy('test')
        const response = await testVideoWebhook()
        setBusy(null)
        if (!response.success || !response.result) {
            toast.error(response.error || 'Não foi possível enviar o teste')
            return
        }
        if (response.result.status === 'SUCCESS') toast.success(`Teste entregue (HTTP ${response.result.lastStatusCode})`)
        else toast.error(`O seu sistema não confirmou: ${response.result.lastError ?? 'sem resposta'}`)
        void loadDeliveries()
    }

    const rotate = async () => {
        setBusy('rotate')
        const response = await rotateVideoWebhookSecret()
        setBusy(null)
        setConfirm(null)
        if (!response.success || !response.result) {
            toast.error(response.error || 'Não foi possível gerar o segredo')
            return
        }
        setSecret(response.result.secret)
    }

    const remove = async () => {
        setBusy('remove')
        const response = await removeVideoWebhook()
        setBusy(null)
        setConfirm(null)
        if (!response.success) {
            toast.error(response.error || 'Não foi possível remover o webhook')
            return
        }
        setExists(false)
        setUrl('')
        setEvents(EVENTS)
        setSecret(null)
        setDeliveries([])
        toast.success('Webhook removido')
    }

    return (
        <section className="card bg-base-100 border border-base-200">
            <div className="card-body p-5 space-y-4">
                <div>
                    <h2 className="font-semibold">Avisos para o seu sistema (webhook)</h2>
                    <p className="text-sm text-base-content/60">
                        O seu sistema recebe um aviso a cada reunião aberta ou encerrada, entrada e saída de participante
                        e gravação pronta. Cada envio é assinado; se o seu sistema não responder, tentamos de novo por
                        até 24 horas.
                    </p>
                </div>

                <div className="form-control w-full">
                    <label className="label pb-1.5" htmlFor="video-webhook-url">
                        <span className="flex w-full items-baseline justify-between gap-3">
                            <span className="label-text font-medium">Endereço do seu sistema</span>
                            <span className="label-text-alt text-base-content/50">https</span>
                        </span>
                    </label>
                    <input
                        id="video-webhook-url"
                        type="url"
                        className="input input-bordered w-full"
                        value={url}
                        placeholder="https://seusistema.com.br/webhooks/video"
                        disabled={!editable}
                        onChange={event => setUrl(event.target.value)}
                    />
                </div>

                <div>
                    <p className="text-sm font-medium mb-2">Eventos</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                        {EVENTS.map(event => (
                            <label key={event} className="label cursor-pointer justify-start gap-3 py-1.5">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-sm checkbox-primary"
                                    checked={events.includes(event)}
                                    disabled={!editable}
                                    onChange={() => toggleEvent(event)}
                                />
                                <span className="label-text">
                                    {VIDEO_WEBHOOK_EVENT_LABELS[event]}
                                    <span className="block font-mono text-xs text-base-content/50">{event}</span>
                                </span>
                            </label>
                        ))}
                    </div>
                </div>

                <label className="label cursor-pointer justify-start gap-3 p-0">
                    <input
                        type="checkbox"
                        className="toggle toggle-primary"
                        checked={enabled}
                        disabled={!editable}
                        onChange={event => setEnabled(event.target.checked)}
                    />
                    <span className="label-text">Envio ligado</span>
                </label>

                {secret && (
                    <div className="rounded-lg border border-warning/40 bg-warning/10 p-3 space-y-2">
                        <p className="text-sm font-medium">Segredo de assinatura</p>
                        <p className="text-xs text-base-content/70">
                            Copie agora: ele não aparece de novo. Use-o no seu sistema para conferir o cabeçalho
                            X-MediarMeet-Signature de cada aviso.
                        </p>
                        <div className="join w-full">
                            <input type="text" className="input input-bordered input-sm join-item w-full font-mono text-xs" value={secret} readOnly />
                            <button
                                type="button"
                                className="btn btn-sm join-item"
                                onClick={() => void navigator.clipboard.writeText(secret).then(() => toast.success('Copiado'))}
                            >
                                <MdContentCopy />
                                Copiar
                            </button>
                        </div>
                    </div>
                )}

                {editable && (
                    <div className="flex flex-wrap justify-end gap-2">
                        {exists && (
                            <>
                                <button type="button" className="btn btn-sm btn-ghost text-error" disabled={busy !== null} onClick={() => setConfirm('remove')}>
                                    <MdDeleteOutline />
                                    Remover
                                </button>
                                <button type="button" className="btn btn-sm btn-ghost" disabled={busy !== null} onClick={() => setConfirm('rotate')}>
                                    <MdKey />
                                    Novo segredo
                                </button>
                                <button type="button" className="btn btn-sm btn-outline" disabled={busy !== null} onClick={test}>
                                    {busy === 'test' ? <span className="loading loading-spinner loading-xs" /> : <MdSend />}
                                    Enviar teste
                                </button>
                            </>
                        )}
                        <button type="button" className="btn btn-sm btn-primary" disabled={busy !== null} onClick={save}>
                            {busy === 'save' ? <span className="loading loading-spinner loading-xs" /> : <MdSave />}
                            Salvar webhook
                        </button>
                    </div>
                )}

                {exists && (
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <h3 className="text-sm font-medium">Últimos envios</h3>
                            <button type="button" className="btn btn-xs btn-ghost" onClick={() => void loadDeliveries()}>
                                <MdRefresh />
                                Atualizar
                            </button>
                        </div>
                        {deliveries.length === 0 ? (
                            <p className="text-sm text-base-content/60">Nenhum envio ainda.</p>
                        ) : (
                            <ul className="divide-y divide-base-200 rounded-lg border border-base-200">
                                {deliveries.slice(0, 20).map(item => (
                                    <li key={item.id} className="px-3 py-2 flex flex-wrap items-center justify-between gap-2 text-sm">
                                        <span className="min-w-0">
                                            <span className="font-mono text-xs">{item.event}</span>
                                            <span className="block text-xs text-base-content/60">
                                                {formatDateTime(item.createdAt)} · {item.attempts} tentativa
                                                {item.attempts === 1 ? '' : 's'}
                                                {item.lastError ? ` · ${item.lastError}` : ''}
                                            </span>
                                        </span>
                                        <span className={`badge badge-sm ${DELIVERY_STATUS[item.status].className}`}>
                                            {DELIVERY_STATUS[item.status].label}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>

            <ConfirmModal
                isOpen={confirm === 'rotate'}
                onClose={() => setConfirm(null)}
                onConfirm={() => void rotate()}
                loading={busy === 'rotate'}
                title="Gerar novo segredo"
                message="O segredo atual deixa de valer na hora. Atualize o seu sistema com o novo segredo para continuar validando os avisos."
                confirmButtonText="Gerar novo segredo"
            />
            <ConfirmModal
                isOpen={confirm === 'remove'}
                onClose={() => setConfirm(null)}
                onConfirm={() => void remove()}
                loading={busy === 'remove'}
                title="Remover webhook"
                message="O seu sistema deixa de receber os avisos do vídeo."
                confirmButtonText="Remover"
            />
        </section>
    )
}
